import { type ChainflipAsset } from '@chainflip/utils/chainflip';
import { ResponsiveLine, type SliceTooltipProps } from '@nivo/line';
import BigNumber from 'bignumber.js';
import { Divider, TokenLogo, TokenWithChainByAsset } from '@/shared/components';
import { ChartTooltip } from '@/shared/components/ChartTooltip';
import GraphLoader from '@/shared/components/GraphLoader';
import { useScreenSize } from '@/shared/hooks';
import { formatUsdValue, chainflipAssetMap, formatWithNumeral, TokenAmount } from '@/shared/utils';
import { getDateAxisTicks } from '@/shared/utils/charts';

type ChartDatum = {
  baseAsset: ChainflipAsset;
  quoteAsset: ChainflipAsset;
  totalBaseLiquidity: string;
  totalQuoteLiquidity: string;
  totalBaseLiquidityValueUsd: string;
  totalQuoteLiquidityValueUsd: string;
  totalLiquidityValueUsd: string;
  date: string;
};

const SliceTooltip = (props: SliceTooltipProps & { data: ChartDatum[] }) => {
  const data = props.data.findLast(
    (item) => new Date(item.date).toISOString().split('T')[0] === props.slice.points[0].data.x,
  );
  if (!data) {
    return null;
  }
  const baseAmount = TokenAmount.fromAsset(data.totalBaseLiquidity, data.baseAsset);
  const quoteAmount = TokenAmount.fromAsset(data.totalQuoteLiquidity, data.quoteAsset);

  return (
    <ChartTooltip activeXAxisItem={props.slice.x}>
      <div className="w-[306px] space-y-1">
        <div className="flex flex-col items-start">
          <div className="text-12 text-cf-light-2">
            {new Date(props.slice.points[0].data.x).toLocaleDateString(undefined, {
              timeZone: 'UTC',
            })}
          </div>
          <div className="text-20 text-cf-white">
            {formatUsdValue(
              BigNumber.sum(...props.slice.points.map((point) => Number(point.data.y))),
            )}
          </div>
          <Divider className="mb-3 mt-1 w-full" />
          <div className="flex w-full flex-col space-y-3 text-12">
            <div className="flex w-full justify-between">
              <div className="flex gap-x-1 text-cf-light-3">
                <TokenWithChainByAsset asset={data.baseAsset} size="small" />
                {chainflipAssetMap[data.baseAsset].name}
              </div>
              <div className="flex text-cf-light-2">
                <div className="text-cf-white">{baseAmount.toFormatted(undefined, 2)}</div> /{' '}
                <div>{formatUsdValue(data.totalBaseLiquidityValueUsd)}</div>
              </div>
            </div>
            <div className="flex w-full justify-between">
              <div className="flex gap-x-2 text-cf-light-3">
                <TokenLogo token={data.quoteAsset} size="small" />
                {chainflipAssetMap[data.quoteAsset].name}
              </div>
              <div className="flex text-cf-light-2">
                <div className="text-cf-white">{quoteAmount.toFormatted(undefined, 2)}</div> /{' '}
                <div>{formatUsdValue(data.totalQuoteLiquidityValueUsd)}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </ChartTooltip>
  );
};

const AreaLineChart = ({
  isLoading,
  chartData,
}: {
  chartData: ChartDatum[];
  isLoading: boolean;
}) => {
  const { isMobile } = useScreenSize();

  const sortedData = chartData.sort((a, b) => (b.date > a.date ? -1 : 1));

  const dates = new Set<string>(
    sortedData.map((item) => new Date(item.date).toISOString().split('T')[0]),
  );
  const chartDates = Array.from(dates);

  const data = [
    {
      id: 'Total Liquidity Value',
      data: chartDates.map((dateString) => ({
        x: dateString,
        y: sortedData.findLast(
          (item) => new Date(item.date).toISOString().split('T')[0] === dateString,
        )?.totalLiquidityValueUsd,
      })),
    },
  ];

  // workaround to make chart usable if there is only a single data point
  if (data[0].data.length === 1) {
    data[0].data = [
      {
        x: new Date(0).toISOString(),
        y: data[0].data[0].y,
      },
      ...data[0].data,
      {
        x: new Date().toISOString(),
        y: data[0].data[0].y,
      },
    ];
  }

  const colors = ['#268AFF'];

  return (
    <GraphLoader className="-mx-4 h-[300px] [&_*]:!font-aeonikRegular" loading={isLoading}>
      <ResponsiveLine
        data={data}
        colors={colors}
        margin={{ top: 20, right: 40, bottom: 50, left: 60 }}
        axisTop={null}
        axisRight={null}
        axisBottom={{
          tickSize: 0,
          tickPadding: 10,
          tickRotation: isMobile ? -60 : 0,
          tickValues: getDateAxisTicks(chartDates, 10),
          format: (dateString) =>
            new Date(dateString).toLocaleDateString(undefined, {
              month: 'short',
              day: 'numeric',
              timeZone: 'UTC',
            }),
        }}
        axisLeft={{
          tickSize: 0,
          tickValues: 6,
          tickPadding: 4,
          format: (number) => `$${formatWithNumeral(number)}`,
        }}
        yScale={{
          type: 'linear',
          max: isLoading ? 50000 : undefined,
          stacked: true,
          nice: true,
        }}
        theme={{
          axis: {
            ticks: {
              text: {
                fill: '#909090',
                fontFamily: 'Aeonik-Medium',
                fontSize: 12,
              },
            },
          },
          grid: {
            line: {
              stroke: '#424242',
              strokeWidth: 1,
              strokeDasharray: 2,
            },
          },
          crosshair: {
            line: {
              stroke: '#FFFFFF',
              strokeOpacity: 0.3,
              strokeWidth: 1,
              strokeDasharray: '2',
            },
          },
        }}
        defs={colors.map((color) => ({
          id: `strategyValueChart-${color}`,
          type: 'linearGradient',
          colors: [
            {
              offset: 0,
              color: `color-mix(in oklab, ${color} 70%, black)`,
            },
            {
              offset: 100,
              color: `color-mix(in oklab, ${color} 50%, black)`,
            },
          ],
        }))}
        fill={colors.map((color) => ({
          match: { color },
          id: `strategyValueChart-${color}`,
        }))}
        sliceTooltip={(props) =>
          SliceTooltip({
            ...props,
            data: sortedData,
          })
        }
        lineWidth={1}
        enableArea
        areaOpacity={0.6}
        enableSlices="x"
        enableGridX={false}
        gridYValues={6}
        role="application"
        enablePoints={false}
        animate={false}
        layers={['grid', 'axes', 'areas', 'lines', 'crosshair', 'slices']}
      />
    </GraphLoader>
  );
};

export const StrategyValueChart = ({ data }: { data?: ChartDatum[] }) => (
  <div className="flex flex-col gap-y-8 rounded-md border border-cf-gray-4 bg-cf-gray-3 p-6 font-aeonikMedium text-16 text-cf-white">
    <div>Strategy Value</div>
    <AreaLineChart isLoading={!data} chartData={data ?? []} />
  </div>
);
